import SparkMD5 from 'spark-md5'
import { completeCloudUploadFile, importResourceSync, initCloudUploadFile } from '@/api/api'
import { getActiveCloudServerKey } from '@/utils/cloudServer'
import { getCloudUploadPageNumber } from '@/utils/cloudUploadPageCount'

export const MAX_CLOUD_UPLOAD_FILE_SIZE = 400 * 1024 * 1024

function extractApiBody(response) {
  if (response && response.data && typeof response.data == 'object') {
    return response.data
  }
  return response || {}
}

function extractApiData(response) {
  const body = extractApiBody(response)
  if (body && typeof body == 'object' && Object.prototype.hasOwnProperty.call(body, 'data')) {
    return body.data || {}
  }
  return body && typeof body == 'object' ? body : {}
}

function assertApiSuccess(response, fallbackMessage) {
  const body = extractApiBody(response)
  if (!body || typeof body != 'object') {
    return body
  }
  const hasApiStatus = Object.prototype.hasOwnProperty.call(body, 'code')
    || Object.prototype.hasOwnProperty.call(body, 'success')
  if (!hasApiStatus) {
    return body
  }
  if (body && (body.code == 200 || body.success === true)) {
    return body
  }
  const message = body && (body.msg || body.message)
    ? (body.msg || body.message)
    : fallbackMessage
  throw new Error(message || 'Upload failed')
}

function pickString(source, keys) {
  if (!source || typeof source != 'object') {
    return ''
  }
  for (let i = 0; i < keys.length; i++) {
    const value = source[keys[i]]
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim()
    }
  }
  return ''
}

function resolveUploadPath(uploadPath) {
  const first = Array.isArray(uploadPath) ? uploadPath[0] : uploadPath
  if (typeof first == 'string') {
    return { url: first, partNumber: '1' }
  }
  if (first && typeof first == 'object') {
    return {
      url: pickString(first, ['url', 'uploadUrl', 'path', 'uploadPath']),
      partNumber: pickString(first, ['partNumber', 'partNo', 'part']) || '1'
    }
  }
  return { url: '', partNumber: '1' }
}

function normalizeFolderType(folderType) {
  return String(folderType || '').trim().replace(/^\/+|\/+$/g, '')
}

function buildUploadFolder(folderType) {
  const normalized = normalizeFolderType(folderType)
  return normalized ? `/${normalized}/` : '/'
}

function getResponseEtag(xhr) {
  const value = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag') || ''
  return String(value).replace(/^"|"$/g, '')
}

function uploadFileToUrl(file, uploadUrl, signal, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let aborted = false
    const handleAbort = () => {
      aborted = true
      xhr.abort()
    }

    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Upload canceled', 'AbortError'))
        return
      }
      signal.addEventListener('abort', handleAbort, { once: true })
    }

    xhr.open('PUT', uploadUrl, true)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    xhr.upload.onprogress = event => {
      if (event.lengthComputable && typeof onProgress == 'function') {
        onProgress({
          loaded: event.loaded,
          total: event.total || file.size || 0
        })
      }
    }
    xhr.onload = () => {
      if (signal) {
        signal.removeEventListener('abort', handleAbort)
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(getResponseEtag(xhr))
        return
      }
      reject(new Error(xhr.statusText || `Upload failed (${xhr.status})`))
    }
    xhr.onerror = () => {
      if (signal) {
        signal.removeEventListener('abort', handleAbort)
      }
      reject(new Error('Upload failed'))
    }
    xhr.onabort = () => {
      if (signal) {
        signal.removeEventListener('abort', handleAbort)
      }
      const error = new DOMException('Upload canceled', 'AbortError')
      if (aborted) {
        reject(error)
      } else {
        reject(error)
      }
    }
    xhr.send(file)
  })
}

function readCloudUploadFileChunk(chunk) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = event => resolve(event && event.target ? event.target.result : null)
    reader.onerror = () => reject(reader.error || new Error('File read failed'))
    reader.readAsArrayBuffer(chunk)
  })
}

async function getCloudUploadFileHash(file) {
  const chunkSize = 100 * 1024 * 1024
  const fileSize = Number(file && file.size) || 0
  const chunks = Math.max(1, Math.ceil(fileSize / chunkSize))
  const spark = new SparkMD5.ArrayBuffer()

  for (let index = 0; index < chunks; index++) {
    const start = index * chunkSize
    const end = Math.min(start + chunkSize, fileSize)
    const buffer = await readCloudUploadFileChunk(file.slice(start, end))
    spark.append(buffer)
  }

  return spark.end()
}

export function isCloudUploadAbort(error) {
  return !!(error && (error.name == 'AbortError' || error.code == 'ERR_CANCELED'))
}

export async function uploadCloudResourceFile(options = {}) {
  const file = options.file
  if (!file) {
    throw new Error('Missing upload file')
  }
  const fileName = options.fileName || file.name
  const folderType = normalizeFolderType(options.folderType)
  const folder = buildUploadFolder(folderType)
  const serverKey = options.serverKey || getActiveCloudServerKey()
  const signal = options.signal
  const appType = Number(options.appType) || 0

  const initResponse = await initCloudUploadFile({
    fileName,
    partNum: 1,
    type: folder
  }, { serverKey, signal })
  assertApiSuccess(initResponse, 'Upload initialization failed')

  const initData = extractApiData(initResponse)
  const uploadPath = resolveUploadPath(initData.uploadPath)
  if (!uploadPath.url) {
    throw new Error('Upload initialization failed')
  }

  const eTag = await uploadFileToUrl(file, uploadPath.url, signal, options.onProgress)
  const eTags = {}
  eTags[String(uploadPath.partNumber || '1')] = eTag
  const hash = await getCloudUploadFileHash(file)

  const completeResponse = await completeCloudUploadFile({
    uploadId: pickString(initData, ['uploadId', 'id']),
    folder,
    source: String(appType),
    filePath: pickString(initData, ['filePath', 'path']),
    fileName,
    hash,
    eTags
  }, { serverKey, signal })
  assertApiSuccess(completeResponse, 'Upload completion failed')

  const completeData = extractApiData(completeResponse)
  const filePath = pickString(completeData, ['fileURL', 'fileUrl', 'file_url', 'filePath', 'path', 'url'])
    || pickString(initData, ['filePath', 'path'])
  if (!filePath) {
    throw new Error('Upload completion failed')
  }
  const pageNumber = await getCloudUploadPageNumber(file, fileName)

  const importResponse = await importResourceSync({
    appType,
    name: fileName,
    parentId: options.parentId || '',
    fileSize: file.size || 0,
    filePath,
    pageNumber
  }, { serverKey, signal })
  assertApiSuccess(importResponse, 'Resource import failed')

  return {
    filePath,
    initData,
    completeData,
    importData: extractApiData(importResponse)
  }
}
